import React, { Component } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Message,
  Divider,
  Table,
  Label,
  Icon,
  Progress,
  Segment,
  Header,
  List,
  Grid,
  Statistic,
} from 'semantic-ui-react';
import _ from 'lodash';
import Papa from 'papaparse';
import axios from 'axios';
import DyzioModal from 'dyzio-shared-components/lib/components/dyzio-modal';
import { processInfluencerCSV } from './instagram-utils';

class CsvInfluencerUploadModal extends Component {
  constructor(props) {
    super(props);
    this.state = {
      step: 'upload', // 'upload', 'processing', 'preview', 'uploading', 'processing-server', 'complete'
      csvFile: null,
      rawData: null,
      processedData: null,
      standardizedCsv: null,
      uploadProgress: 0,
      uploadedFileUrl: null,
      error: null,
    };
  }

  handleFileChange = event => {
    const file = _.get(event, 'target.files[0]');
    if (!file) return;

    // Clear any previous errors
    this.setState({ error: null });

    if (!_.endsWith(_.toLower(file.name), '.csv')) {
      this.setState({
        error: 'Please select a CSV file',
        step: 'upload',
      });
      // Clear the file input
      event.target.value = '';
      return;
    }

    this.setState({
      csvFile: file,
      step: 'processing',
      error: null,
    });

    Papa.parse(file, {
      header: true,
      dynamicTyping: true,
      skipEmptyLines: true,
      delimiter: '', // Let Papa Parse auto-detect, but don't error on warnings
      complete: results => {
        // Only error on actual parsing errors, not delimiter detection warnings
        const realErrors = results.errors.filter(error =>
          error.type !== 'Delimiter' && error.type !== 'FieldMismatch',
        );
        if (realErrors.length > 0) {
          this.setState({
            step: 'upload',
            error: `CSV parsing error: ${_.get(realErrors, '[0].message', 'Unknown error')}`,
          });
          return;
        }

        if (!results.data || _.size(results.data) === 0) {
          this.setState({
            step: 'upload',
            error: 'CSV file appears to be empty',
          });
          return;
        }

        const processedData = processInfluencerCSV(results.data);

        this.setState({
          rawData: results.data,
          processedData,
          step: 'preview',
          error: null,
        });
      },
      error: error => {
        this.setState({
          step: 'upload',
          error: `Error reading file: ${_.get(error, 'message', 'Unknown error')}`,
        });
      },
    });
  };

  generateStandardizedCSV = validInfluencers => {
    const { campaignId } = this.props;
    // Remove duplicates first, then take only first 150
    const uniqueInfluencers = _.uniqBy(validInfluencers, 'instagramHandle');
    const limitedInfluencers = _.take(uniqueInfluencers, 150);

    // Create standardized CSV data with canonical Instagram URLs
    const csvData = _.map(limitedInfluencers, influencer => ({
      instagramUrl: `https://instagram.com/${influencer.instagramHandle}`,
    }));

    // Convert to CSV string
    const csv = Papa.unparse(csvData, {
      header: true,
      columns: ['instagramUrl'],
    });

    // Create blob for upload with timestamp in filename
    const timestamp = Date.now();
    const fileName = `${campaignId}-influencers-${timestamp}.csv`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const file = new File([blob], fileName, { type: 'text/csv' });

    return { csvString: csv, file, data: csvData, fileName };
  };

  uploadToS3 = async (file, fileName) => {
    try {
      const s3Url = `https://s3-eu-west-1.amazonaws.com/discussions-uploads-production/${fileName}`;

      await axios.put(s3Url, file, {
        headers: {
          'Content-Type': 'text/csv',
        },
        onUploadProgress: progressEvent => {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          this.setState({ uploadProgress: progress });
        },
      });

      return {
        url: s3Url,
        fileName,
      };
    } catch (error) {
      throw new Error(`S3 upload failed: ${error.message}`);
    }
  };

  processInfluencersOnServer = async s3Url => {
    const { campaignId, userEmail } = this.props;

    try {
      const response = await axios.post(
        `https://papi.dyzio.co/papi/secure/campaign/${campaignId}/csv`,
        {
          data: {
            filename: s3Url,
            userEmail,
          },
        },
        {
          headers: {
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    } catch (error) {
      throw new Error(`Server processing failed: ${error.message}`);
    }
  };

  handleUploadToS3 = async () => {
    const { processedData } = this.state;

    if (!processedData || processedData.summary.valid === 0) {
      return;
    }

    const validInfluencers = _.filter(processedData.results, 'isValid');
    const standardizedCsv = this.generateStandardizedCSV(validInfluencers);

    this.setState({
      step: 'uploading',
      standardizedCsv,
      uploadProgress: 0,
    });

    try {
      // Step 1: Upload to S3
      const uploadResult = await this.uploadToS3(standardizedCsv.file, standardizedCsv.fileName);

      this.setState({
        uploadedFileUrl: uploadResult.url,
        step: 'processing-server',
      });

      // Step 2: Fire and forget - start server processing but don't wait
      this.processInfluencersOnServer(uploadResult.url).catch(error => {
        console.error('Server processing error (expected timeout):', error);
      });

      // Immediately show completion
      this.setState({
        step: 'complete',
      });
    } catch (error) {
      this.setState({
        step: 'preview',
        error: `Upload failed: ${error.message}`,
      });
    }
  };

  resetUpload = () => {
    this.setState({
      step: 'upload',
      csvFile: null,
      rawData: null,
      processedData: null,
      standardizedCsv: null,
      uploadProgress: 0,
      uploadedFileUrl: null,
      error: null,
    });
  };

  renderUploadStep = () => (
    <div>
      <Header as="h3">
        <Icon name="upload" />
        Upload Influencer CSV
      </Header>

      <p>Upload a CSV file containing influencer data. The system will automatically detect and validate Instagram handles from various formats.</p>

      <Segment placeholder>
        <table width="100%">
          <tr>
            <td>
              <Header icon>
                <Icon name="file outline" />
                Choose your CSV file
              </Header>
            </td>
            <td>
              <Button primary as="label" htmlFor="csv-file-input">
                <Icon name="upload" />
                Select CSV File
              </Button>
              <input
                id="csv-file-input"
                key={this.state.step} // Reset input when step changes
                type="file"
                accept=".csv"
                onChange={this.handleFileChange}
                style={{ display: 'none' }}
              />
            </td>
          </tr>
        </table>
      </Segment>

      <Message info>
        <Message.Header>CSV Format Requirements</Message.Header>
        <List bulleted>
          <List.Item><strong>MAXIMUM of 150 Influencers. INSTAGRAM only.</strong></List.Item>
          <List.Item><strong>Single column:</strong> One column with Instagram handles or URLs</List.Item>
          <List.Item><strong>Header:</strong> Any header name (will be ignored)</List.Item>
          <List.Item><strong>Data:</strong> One Instagram handle per row starting from row 2</List.Item>
        </List>
      </Message>

      <Message>
        <Message.Header>Supported Instagram Formats</Message.Header>
        <List bulleted>
          <List.Item>Simple handles: <code>kimkardashian</code></List.Item>
          <List.Item>With @ symbol: <code>@kimkardashian</code></List.Item>
          <List.Item>Full URLs: <code>https://instagram.com/kimkardashian</code></List.Item>
          <List.Item>Short URLs: <code>instagram.com/kimkardashian</code></List.Item>
        </List>
      </Message>
    </div>
  );

  renderProcessingStep = () => (
    <div>
      <Header as="h3">
        <Icon name="cog" loading />
        Processing CSV Data
      </Header>
      <Progress percent={50} indicating>
        Extracting and validating Instagram handles...
      </Progress>
    </div>
  );

  renderPreviewStep = () => {
    const { processedData } = this.state;

    if (!processedData) return null;

    const { results, summary } = processedData;
    const validResults = _.filter(results, 'isValid');
    const invalidResults = _.reject(results, 'isValid');
    const sampleValid = _.take(validResults, 5);
    const sampleInvalid = _.take(invalidResults, 3);

    return (
      <div>
        <Header as="h3">
          <Icon name="chart bar" />
          Validation Results
        </Header>

        {summary.overLimit && (
          <Message negative>
            <Message.Header>Too Many Influencers</Message.Header>
            <p>
              You have {summary.validAfterDedup} unique valid influencers, but the maximum allowed is 150.
              Please reduce your list by {summary.validAfterDedup - 150} influencers and try again.
            </p>
          </Message>
        )}

        <Grid columns={4} divided>
          <Grid.Column>
            <Statistic color="green" size="small">
              <Statistic.Value>{summary.valid}</Statistic.Value>
              <Statistic.Label>Valid</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic color="red" size="small">
              <Statistic.Value>{summary.invalid}</Statistic.Value>
              <Statistic.Label>Invalid</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic color="orange" size="small">
              <Statistic.Value>{summary.withWarnings}</Statistic.Value>
              <Statistic.Label>Warnings</Statistic.Label>
            </Statistic>
          </Grid.Column>
          <Grid.Column>
            <Statistic color="purple" size="small">
              <Statistic.Value>{summary.duplicates}</Statistic.Value>
              <Statistic.Label>Duplicates</Statistic.Label>
            </Statistic>
          </Grid.Column>
        </Grid>

        <Divider />

        {summary.valid > 0 && (
          <div>
            <Header as="h4" color="green">
              <Icon name="check circle" />
              Valid Influencers (showing first 5)
            </Header>
            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Row</Table.HeaderCell>
                  <Table.HeaderCell>Original Input</Table.HeaderCell>
                  <Table.HeaderCell>Instagram Handle</Table.HeaderCell>
                  <Table.HeaderCell>Warnings</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {_.map(sampleValid, (row, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{row.originalRow}</Table.Cell>
                    <Table.Cell><code>{row.originalInstagramInput}</code></Table.Cell>
                    <Table.Cell>
                      <Label color="green">
                        <Icon name="instagram" />
                        @{row.instagramHandle}
                      </Label>
                    </Table.Cell>
                    <Table.Cell>
                      {_.map(row.validationWarnings || [], (warning, i) => (
                        <Label key={i} color="orange" size="tiny">
                          <Icon name="warning" />
                          {warning}
                        </Label>
                      ))}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {_.size(validResults) > 5 && (
              <Message info>
                <p>... and {_.size(validResults) - 5} more valid influencers</p>
              </Message>
            )}
          </div>
        )}

        {summary.invalid > 0 && (
          <div>
            <Header as="h4" color="red">
              <Icon name="x circle" />
              Invalid Rows (showing first 3)
            </Header>
            <Table celled compact>
              <Table.Header>
                <Table.Row>
                  <Table.HeaderCell>Row</Table.HeaderCell>
                  <Table.HeaderCell>Input</Table.HeaderCell>
                  <Table.HeaderCell>Error</Table.HeaderCell>
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {_.map(sampleInvalid, (row, index) => (
                  <Table.Row key={index}>
                    <Table.Cell>{row.originalRow}</Table.Cell>
                    <Table.Cell>{row.originalInstagramInput || 'No Instagram data'}</Table.Cell>
                    <Table.Cell style={{ color: 'red' }}>
                      <Icon name="x" />
                      {row.validationError}
                    </Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table>
            {_.size(invalidResults) > 3 && (
              <Message warning>
                <p>... and {_.size(invalidResults) - 3} more invalid rows</p>
              </Message>
            )}
          </div>
        )}

        {summary.valid === 0 && (
          <Message negative>
            <Message.Header>No valid influencers found</Message.Header>
            <p>Please check your CSV format and try again. Make sure you have a column with Instagram handles or URLs.</p>
          </Message>
        )}

        {summary.valid > 0 && (
          <div>
            <Divider />
            <Header as="h4">
              <Icon name="file text" />
              Standardized Output Preview
            </Header>
            <Message info>
              <p>The following standardized CSV will be uploaded:</p>
            </Message>
            <Segment>
              <pre style={{ fontSize: '12px', overflow: 'auto', maxHeight: '200px' }}>
                {this.generateStandardizedCSV(_.filter(results, 'isValid')).csvString}
              </pre>
            </Segment>
          </div>
        )}
      </div>
    );
  };

  renderUploadingStep = () => {
    const { standardizedCsv, uploadProgress } = this.state;

    return (
      <div>
        <Header as="h3">
          <Icon name="cloud upload" color="blue" />
          Uploading Standardized CSV
        </Header>

        <Progress percent={uploadProgress} indicating active>
          Uploading to S3... {uploadProgress}%
        </Progress>

        {standardizedCsv && (
          <Message info>
            <Message.Header>Uploading directly to S3</Message.Header>
            <p>Converting {_.size(standardizedCsv.data)} valid influencers to canonical Instagram URLs.</p>
            <p><strong>File:</strong> {standardizedCsv.fileName}</p>
            <p><strong>Size:</strong> {Math.round(standardizedCsv.file.size / 1024)} KB</p>
            <p><strong>S3 Bucket:</strong> discussions-uploads-production</p>
          </Message>
        )}
      </div>
    );
  };

  renderProcessingServerStep = () => (
    <div>
      <Header as="h3">
        <Icon name="server" color="blue" />
        Starting Processing
      </Header>

      <Progress percent={95} indicating active>
        Initiating server processing...
      </Progress>

      <Message info>
        <Message.Header>Starting background processing</Message.Header>
        <p>Your CSV has been uploaded successfully. Starting the processing job now...</p>
      </Message>
    </div>
  );

  renderCompleteStep = () => {
    const { uploadedFileUrl } = this.state;
    const { userEmail } = this.props;

    return (
      <div>
        <Header as="h3">
          <Icon name="check circle" color="green" />
          Upload Complete
        </Header>
        <Progress percent={100} color="green">
          Upload successful!
        </Progress>
        <Message success>
          <Message.Header>Upload successful</Message.Header>
          <p><strong>Your influencers are being processed now.</strong></p>
          <p>There can still be errors if handles cannot be found. You will be emailed a detailed status report at <strong>{userEmail}</strong>.</p>

          {uploadedFileUrl && (
            <p style={{ fontSize: '12px', color: '#666', marginTop: '10px' }}>
              <strong>File uploaded:</strong> {uploadedFileUrl.split('/').pop()}
            </p>
          )}
        </Message>
      </div>
    );
  };

  render() {
    const { onClose, isSubmitting } = this.props;
    const { step, processedData, error } = this.state;

    const canImport = processedData && _.get(processedData, 'summary.valid', 0) > 0 && !_.get(processedData, 'summary.overLimit', false);

    return (
      <DyzioModal
        headerText="Import Influencers from CSV"
        size="large"
        onClose={onClose}
      >
        <div>
          {error && (
            <Message negative>
              <Message.Header>Error</Message.Header>
              <p>{error}</p>
            </Message>
          )}

          {step === 'upload' && this.renderUploadStep()}
          {step === 'processing' && this.renderProcessingStep()}
          {step === 'preview' && this.renderPreviewStep()}
          {step === 'uploading' && this.renderUploadingStep()}
          {step === 'processing-server' && this.renderProcessingServerStep()}
          {step === 'complete' && this.renderCompleteStep()}

          <Divider />

          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            {step === 'preview' && (
              <Button
                onClick={this.resetUpload}
                disabled={isSubmitting}
              >
                <Icon name="arrow left" />
                Upload Different File
              </Button>
            )}

            {step !== 'preview' && step !== 'complete' && <div />}

            {step === 'preview' && (
              <Button
                color="pink"
                disabled={!canImport || isSubmitting}
                loading={isSubmitting}
                onClick={this.handleUploadToS3}
                size="medium"
              >
                <Icon name="cloud upload" />
                Upload {_.get(processedData, 'summary.valid', 0)} Influencers to S3
              </Button>
            )}

            {step === 'complete' && (
              <Button
                color="green"
                onClick={onClose}
                size="medium"
                style={{ margin: '0 auto' }}
              >
                <Icon name="check" />
                OK
              </Button>
            )}
          </div>
        </div>
      </DyzioModal>
    );
  }
}

CsvInfluencerUploadModal.propTypes = {
  onClose: PropTypes.func,
  onSubmit: PropTypes.func,
  isSubmitting: PropTypes.bool,
  campaignId: PropTypes.string,
  userEmail: PropTypes.string,
};

CsvInfluencerUploadModal.defaultProps = {
  onClose: _.noop,
  onSubmit: _.noop,
  isSubmitting: false,
};

export default CsvInfluencerUploadModal;



/** WEBPACK FOOTER **
 ** ./src/components/csv-upload-modal/index.js
 **/